import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { ArrowUpDown, Download, Info, Search } from "lucide-react";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

type Tier = "Gold" | "Partner" | "Standard";
type Action = "upgrade" | "downgrade" | "hold" | "ok";
type Movement = "up" | "down" | "none";

interface Row {
    senta_id: string;
    client_name: string;
    client_ref: string | null;
    manager: string | null;
    flag_tier: Tier;
    fee_tier: Tier;
    prior_tier: Tier;
    contracted_value: number;
    contracted_value_prior: number;
    contracted_change: number;
    trailing_12m: number;
    active_templates: number;
    last_recurring_invoice: string | null;
    action: Action;
    action_tier: Tier;
    movement: Movement;
}

interface Payload {
    as_of: string;
    prior_as_of: string;
    lookback_months: number;
    thresholds: { gold: number; partner: number; buffer: number };
    summary: {
        upgrade_gold: number; upgrade_partner: number;
        downgrade_gold: number; downgrade_partner: number;
        hold: number; crossed_up: number; crossed_down: number;
    };
    count: number;
    rows: Row[];
}

type View = "all" | "upgrade" | "downgrade" | "hold" | "crossed";

const eur = (n: number) =>
    n.toLocaleString("en-IE", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (d: string) => new Date(d).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" });

const TIER_COLOR: Record<Tier, string> = { Gold: "#d29922", Partner: "#58a6ff", Standard: "var(--text-muted)" };
const TierBadge: React.FC<{ tier: Tier }> = ({ tier }) => (
    <span style={{ color: TIER_COLOR[tier], fontWeight: 600 }}>{tier}</span>
);

const customStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: { style: { backgroundColor: "var(--bg-tertiary)", borderBottomColor: "var(--border-color)", color: "var(--text-primary)", fontWeight: 600, fontSize: "0.85rem" } },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)", borderBottomColor: "var(--border-color)" },
        highlightOnHoverStyle: { backgroundColor: "var(--bg-hover)", color: "var(--text-primary)", borderBottomColor: "var(--border-color)" },
    },
    cells: { style: { color: "var(--text-primary)", paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)", borderTopColor: "var(--border-color)" },
        pageButtonsStyle: { color: "var(--text-secondary)", fill: "var(--text-secondary)", "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" }, "&:disabled": { opacity: 0.4 } },
    },
    noData: { style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" } },
};

const ClientTierMovements: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();

    const [payload, setPayload] = useState<Payload | null>(null);
    const [lookback, setLookback] = useState<number>(3);
    const [view, setView] = useState<View>("all");
    const [search, setSearch] = useState("");
    // Explanation is open by default — the tier logic needs to be understood
    // before anyone acts on a recommendation.
    const [showHelp, setShowHelp] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) { setError("Your session has expired — please refresh the page to sign in again."); return; }
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${getApiBaseUrl()}/api/client-tier-movements/data?lookbackMonths=${lookback}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`Request failed (${res.status})`);
                setPayload(await res.json());
            } catch (err: any) {
                setError(err?.message || "Failed to load tier movements");
                setPayload(null);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [instance, accounts, lookback, setLoading]);

    const rows = useMemo(() => {
        let all = payload?.rows || [];
        if (view === "upgrade") all = all.filter((r) => r.action === "upgrade");
        else if (view === "downgrade") all = all.filter((r) => r.action === "downgrade");
        else if (view === "hold") all = all.filter((r) => r.action === "hold");
        else if (view === "crossed") all = all.filter((r) => r.movement !== "none");
        if (search) {
            const q = search.toLowerCase();
            all = all.filter((r) =>
                r.client_name?.toLowerCase().includes(q) ||
                r.senta_id?.toLowerCase().includes(q) ||
                (r.client_ref || "").toLowerCase().includes(q) ||
                (r.manager || "").toLowerCase().includes(q));
        }
        // Upgrades first, then downgrades, then holds; biggest contracted value first within each.
        const order: Record<Action, number> = { upgrade: 0, downgrade: 1, hold: 2, ok: 3 };
        return [...all].sort((a, b) => order[a.action] - order[b.action] || b.contracted_value - a.contracted_value);
    }, [payload, view, search]);

    const th = payload?.thresholds || { gold: 10000, partner: 3000, buffer: 0.05 };
    const goldFloor = Math.round(th.gold * (1 - th.buffer));
    const partnerFloor = Math.round(th.partner * (1 - th.buffer));

    const actionLabel = (r: Row) => {
        if (r.action === "upgrade") return `Upgrade to ${r.action_tier}`;
        if (r.action === "downgrade") return `Downgrade to ${r.action_tier}`;
        if (r.action === "hold") return "Within buffer — hold";
        return "OK";
    };
    const actionColor = (a: Action) => a === "upgrade" ? "#2ea043" : a === "downgrade" ? "#f85149" : a === "hold" ? "#d29922" : "var(--text-muted)";

    const columns: TableColumn<Row>[] = [
        {
            name: "Client", selector: (r) => r.client_name || "", sortable: true, grow: 3,
            cell: (r) => (
                <div>
                    <div className="cell-primary">{r.client_name || "—"}</div>
                    <div className="cell-secondary" style={{ fontSize: 11 }}>
                        <code>{r.senta_id}</code>{r.client_ref ? ` · ${r.client_ref}` : ""}{r.manager ? ` · ${r.manager}` : ""}
                    </div>
                </div>
            ),
        },
        {
            name: "Action", selector: (r) => r.action, sortable: true, width: "190px",
            cell: (r) => <span style={{ color: actionColor(r.action), fontWeight: 600 }}>{actionLabel(r)}</span>,
        },
        { name: "Senta Tier", selector: (r) => r.flag_tier, sortable: true, width: "110px", cell: (r) => <TierBadge tier={r.flag_tier} /> },
        { name: "Tier by Fees", selector: (r) => r.fee_tier, sortable: true, width: "115px", cell: (r) => <TierBadge tier={r.fee_tier} /> },
        {
            name: "Contracted Value", selector: (r) => r.contracted_value, sortable: true, right: true, width: "140px",
            cell: (r) => (
                <span className="cell-value" title={r.active_templates ? `${r.active_templates} active recurring template(s); last invoiced ${r.last_recurring_invoice ? fmtDate(r.last_recurring_invoice) : "—"}` : "No active recurring invoices"}>
                    {r.contracted_value ? eur(r.contracted_value) : "—"}
                </span>
            ),
        },
        {
            name: `${payload?.lookback_months ?? lookback}m ago`, selector: (r) => r.contracted_value_prior, sortable: true, right: true, width: "110px",
            cell: (r) => <span className="cell-secondary">{r.contracted_value_prior ? eur(r.contracted_value_prior) : "—"}</span>,
        },
        {
            name: "Change", selector: (r) => r.contracted_change, sortable: true, right: true, width: "120px",
            cell: (r) => (
                <span style={{ color: r.contracted_change > 0 ? "#2ea043" : r.contracted_change < 0 ? "#f85149" : "var(--text-muted)", fontWeight: 600 }}>
                    {r.contracted_change === 0 ? "—" : (r.contracted_change > 0 ? "+" : "") + eur(r.contracted_change)}
                    {r.movement !== "none" && <span title={`Crossed ${r.movement} from ${r.prior_tier} to ${r.fee_tier} within the lookback`}> {r.movement === "up" ? "▲" : "▼"}</span>}
                </span>
            ),
        },
        {
            name: "Invoiced 12m", selector: (r) => r.trailing_12m, sortable: true, right: true, width: "120px",
            cell: (r) => <span className="cell-secondary" title="Everything invoiced in the last 12 months, incl. one-offs — context only">{eur(r.trailing_12m)}</span>,
        },
    ];

    const exportRows = () => rows.map((r) => ({
        senta_id: r.senta_id, client_name: r.client_name, client_ref: r.client_ref || "", manager: r.manager || "",
        action: actionLabel(r), senta_tier: r.flag_tier, tier_by_fees: r.fee_tier,
        contracted_value: r.contracted_value, contracted_value_prior: r.contracted_value_prior, change: r.contracted_change,
        crossed: r.movement === "none" ? "" : r.movement, invoiced_12m: r.trailing_12m,
        active_recurring_templates: r.active_templates, last_recurring_invoice: r.last_recurring_invoice || "",
    }));
    const downloadCSV = () => {
        const data = exportRows(); const headers = Object.keys(data[0] || { senta_id: "" });
        const esc = (v: any) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
        const csv = [headers.join(","), ...data.map((r) => headers.map((h) => esc((r as any)[h])).join(","))].join("\n");
        const link = document.createElement("a");
        link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        link.download = `client_tier_movements_${payload?.as_of || "export"}.csv`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };
    const downloadXLSX = () => {
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows()), "Movements");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
            { Metric: "As of", Value: payload?.as_of || "" },
            { Metric: "Compared with", Value: payload?.prior_as_of || "" },
            { Metric: "Gold threshold (EUR)", Value: th.gold },
            { Metric: "Partner threshold (EUR)", Value: th.partner },
            { Metric: "Downgrade buffer", Value: `${Math.round(th.buffer * 100)}%` },
            { Metric: "Upgrade to Gold", Value: payload?.summary.upgrade_gold ?? 0 },
            { Metric: "Upgrade to Partner", Value: payload?.summary.upgrade_partner ?? 0 },
            { Metric: "Downgrade from Gold", Value: payload?.summary.downgrade_gold ?? 0 },
            { Metric: "Downgrade from Partner", Value: payload?.summary.downgrade_partner ?? 0 },
        ]), "Summary");
        XLSX.writeFile(wb, `client_tier_movements_${payload?.as_of || "export"}.xlsx`);
    };

    const s = payload?.summary;
    const chip = (v: View, label: string) => {
        const active = view === v;
        return (
            <button key={v} onClick={() => setView(v)} style={{ padding: "5px 12px", border: "1px solid var(--border-color)", borderRadius: 16, background: active ? "var(--accent-primary)" : "var(--bg-secondary)", color: active ? "#fff" : "var(--text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: active ? 600 : 400 }}>
                {label}
            </button>
        );
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="page-title"><ArrowUpDown size={20} /><h1>Client Tier Movements</h1></div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button className="download-btn" onClick={() => setShowHelp((v) => !v)}><Info size={16} /> {showHelp ? "Hide notes" : "How this works"}</button>
                        <button className="download-btn" onClick={downloadCSV} disabled={!rows.length}><Download size={16} /> CSV</button>
                        <button className="download-btn" onClick={downloadXLSX} disabled={!rows.length}><Download size={16} /> XLSX</button>
                    </div>
                </div>
            </div>

            {showHelp && (
                <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", borderLeft: "3px solid var(--accent-primary)", borderRadius: 6, padding: "14px 18px", marginBottom: 20, color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.55 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, color: "var(--text-primary)", fontWeight: 600, fontSize: 14 }}><Info size={16} /> How this works</div>
                    <ul style={{ margin: "0 0 0 20px", padding: 0 }}>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Contracted value</strong> is the client's annual recurring fee. It is
                            built from the <strong>Xero repeating invoices</strong> synced into MKP: for each repeating-invoice template that is still
                            active for the client, we take the most recent invoice amount (net of VAT) and annualise it by the template's
                            cadence — monthly × 12, quarterly × 4, half-yearly × 2, annual × 1. "Active" means it has produced an invoice within
                            roughly the last two cycles. One-off invoices are excluded; they are shown separately in the <em>Invoiced 12m</em> column
                            for context only.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Tiers</strong> — Gold Partner at contracted value of{" "}
                            <strong>{eur(th.gold)}+</strong>, Partner at <strong>{eur(th.partner)}+</strong>, otherwise Standard. The{" "}
                            <em>Senta Tier</em> column is the Gold Partner / Partner flag currently set in Senta; <em>Tier by Fees</em> is what the
                            contracted value says it should be.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>{Math.round(th.buffer * 100)}% buffer on downgrades</strong> — a client is
                            only recommended for downgrade once their contracted value falls below <strong>{eur(goldFloor)}</strong> (Gold) or{" "}
                            <strong>{eur(partnerFloor)}</strong> (Partner). Between the buffer and the threshold they show as{" "}
                            <em>Within buffer — hold</em>, so a client sitting on the line doesn't flip tier every month. Upgrades use the full threshold.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Crossings (▲ ▼)</strong> compare today's contracted value with the value{" "}
                            {payload?.lookback_months ?? lookback} months ago ({payload ? fmtDate(payload.prior_as_of) : "—"}), so you can see tier
                            movement as it happens, not just the backlog. Change the lookback above to widen or narrow it.
                        </li>
                        <li>
                            Only clients needing attention appear here — a recommended upgrade or downgrade, a hold inside the buffer, or a crossing
                            within the lookback. Clients whose Senta tier already matches their fees are not listed. Archived clients are excluded.
                        </li>
                    </ul>
                </div>
            )}

            <div className="table-controls" style={{ marginBottom: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-secondary)", fontSize: 13 }}>
                    Compare with:
                    <select value={lookback} onChange={(e) => setLookback(parseInt(e.target.value, 10))} className="filter-select" style={{ padding: "6px 10px", borderRadius: 4 }}>
                        {[1, 3, 6, 12].map((m) => <option key={m} value={m}>{m} month{m > 1 ? "s" : ""} ago</option>)}
                    </select>
                </label>
                <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180, maxWidth: 320 }}>
                    <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: "var(--text-secondary)" }} />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client, ref, manager…"
                        style={{ width: "100%", padding: "8px 10px 8px 30px", borderRadius: 6, border: "1px solid var(--border-color)", background: "var(--bg-primary)", color: "var(--text-primary)" }} />
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 12 }}>
                <Kpi label="Upgrade to Gold" value={s?.upgrade_gold ?? 0} color="#2ea043" onClick={() => setView("upgrade")} active={view === "upgrade"} />
                <Kpi label="Upgrade to Partner" value={s?.upgrade_partner ?? 0} color="#2ea043" onClick={() => setView("upgrade")} active={view === "upgrade"} />
                <Kpi label="Downgrade from Gold" value={s?.downgrade_gold ?? 0} color="#f85149" onClick={() => setView("downgrade")} active={view === "downgrade"} />
                <Kpi label="Downgrade from Partner" value={s?.downgrade_partner ?? 0} color="#f85149" onClick={() => setView("downgrade")} active={view === "downgrade"} />
                <Kpi label="Within buffer" value={s?.hold ?? 0} color="#d29922" onClick={() => setView("hold")} active={view === "hold"} />
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 12 }}>
                {chip("all", `All (${payload?.count ?? 0})`)}
                {chip("upgrade", "Upgrades")}
                {chip("downgrade", "Downgrades")}
                {chip("hold", "Within buffer")}
                {chip("crossed", `Crossed in last ${payload?.lookback_months ?? lookback}m (${(s?.crossed_up ?? 0) + (s?.crossed_down ?? 0)})`)}
                {payload && <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-secondary)" }}>As of {fmtDate(payload.as_of)} · compared with {fmtDate(payload.prior_as_of)}</span>}
            </div>

            {error && <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--status-error, #d9534f)", borderRadius: 6, padding: "10px 14px", marginBottom: 12, color: "var(--status-error, #d9534f)" }}>{error}</div>}

            <div className="tier-table" style={{ background: "var(--bg-secondary)", borderRadius: 8, border: "1px solid var(--border-color)" }}>
                <style>{`
                    .tier-table .row-upgrade, .tier-table .row-upgrade:hover { background-color: rgba(46, 160, 67, 0.10) !important; border-left: 3px solid #2ea043; }
                    .tier-table .row-downgrade, .tier-table .row-downgrade:hover { background-color: rgba(239, 68, 68, 0.12) !important; border-left: 3px solid #ef4444; }
                    .tier-table .row-hold, .tier-table .row-hold:hover { border-left: 3px solid #d29922; }
                `}</style>
                <DataTable
                    columns={columns}
                    data={rows}
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    conditionalRowStyles={[
                        { when: (r: Row) => r.action === "upgrade", classNames: ["row-upgrade"] },
                        { when: (r: Row) => r.action === "downgrade", classNames: ["row-downgrade"] },
                        { when: (r: Row) => r.action === "hold", classNames: ["row-hold"] },
                    ]}
                    noDataComponent={
                        <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                            {payload === null ? "Loading…" : "No clients match this view."}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

const Kpi: React.FC<{ label: string; value: number; color: string; onClick: () => void; active: boolean }> = ({ label, value, color, onClick, active }) => (
    <button onClick={onClick} style={{ textAlign: "left", background: "var(--bg-secondary)", border: `1px solid ${active ? color : "var(--border-color)"}`, borderRadius: 8, padding: 14, cursor: "pointer" }}>
        <div style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 600, color }}>{value}</div>
    </button>
);

export default ClientTierMovements;
